import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Phone, MapPin, Instagram, Facebook, ShoppingBag, Store, Share2, ChevronRight, Sparkles, Star, Zap, Copy, Check, Download, X, MessageCircle, Plus, Minus, ClipboardList, Megaphone, LayoutDashboard } from 'lucide-react'
import { useEffect, useState, useRef } from 'react'
import { toPng } from 'html-to-image'
import { incrementBrandView, trackWhatsAppClick, trackProductView } from '../hooks/useApi'
import { LazadaIcon, ShopeeIcon, TokopediaIcon } from '../components/MarketplaceIcons'
import { FoodDeliveryIcon } from '../components/FoodDeliveryIcons'
import { getFoodChannels, openFoodStore, type FoodChannel } from '../lib/foodDelivery'
import { toast } from 'sonner'
import { applyCatalogSeo, clearCatalogSeo } from '../lib/catalogSeo'
import { getImageUrl, getProductCatalogUrl } from '../lib/catalogUrl'
import { normalizePlanId, canUseFeature } from '../lib/plans'
import { useAuth } from '../hooks/authContext'

const DEFAULT_THEME = '#3b82f6'

const API_URL = import.meta.env.VITE_API_URL || '/api'

// Helper function to format Rupiah
const formatRupiah = (price: number | string | undefined) => {
  if (!price) return 'Rp 0'
  const num = typeof price === 'string' ? parseInt(price) : price
  if (isNaN(num)) return 'Rp 0'
  return `Rp ${num.toLocaleString('id-ID').replace(/,/g, '.')}`
}

interface Brand {
  id: number
  name: string
  slug: string
  logoUrl?: string
  address?: string
  description?: string
  whatsapp?: string
  instagram?: string
  facebook?: string
  tiktok?: string
  tokopediaUrl?: string | null
  shopeeUrl?: string | null
  lazadaUrl?: string | null
  grabFoodUrl?: string | null
  goFoodUrl?: string | null
  shopeeFoodUrl?: string | null
  hasGrabFood?: boolean
  hasGoFood?: boolean
  hasShopeeFood?: boolean
  colorTheme?: string
  promoBannerUrl?: string | null
  promoVideoUrl?: string | null
  subdomain?: string | null
  plan?: string
  badge?: string | null
  canonicalUrl?: string | null
  subdomainUrl?: string | null
  owner?: {
    id: number
    name: string
    email: string
  }
}

interface Product {
  id: number
  name: string
  slug?: string | null
  description?: string
  price: number
  discountPrice?: number
  images?: string[]
  brandId: number
  brandName?: string
  brandSlug?: string
  isActive?: boolean
}

interface BrandCategory {
  id: number
  name: string
  slug: string
  logoUrl?: string | null
  description?: string | null
  isPrimary?: boolean
  products: Product[]
}

interface GroupedCatalog {
  brand: Brand
  categories: BrandCategory[]
  products: Product[]
  activeCategorySlug?: string | null
}

type CartItem = { product: Product; quantity: number }

const getUnitPrice = (product: Product) => {
  const price = Number(product.price) || 0
  const discount = product.discountPrice != null ? Number(product.discountPrice) : null
  if (discount != null && discount < price) return discount
  return price
}

const THEME_STYLES: Record<string, {
  pageBg: string
  navbarBorder: string
  heroBg: string
  heroBorder: string
  heroBadgeBg: string
  heroBadgeText: string
  primaryButtonBg: string
  primaryButtonHover: string
  ctaSectionBg: string
  ctaButtonText: string
}> = {
  '#3b82f6': {
    pageBg: 'bg-[#f8f6f2]',
    navbarBorder: 'border-[#e4c47a]',
    heroBg: 'bg-[#ffd76a]',
    heroBorder: 'border-[#f3c74e]',
    heroBadgeBg: 'bg-[#f6b80f]',
    heroBadgeText: 'text-slate-900',
    primaryButtonBg: 'bg-[#102a5c]',
    primaryButtonHover: 'hover:bg-[#0d2349]',
    ctaSectionBg: 'bg-[#0f2b60]',
    ctaButtonText: 'text-[#0f2b60]',
  },
  '#0f766e': {
    pageBg: 'bg-[#f2f8f7]',
    navbarBorder: 'border-emerald-300',
    heroBg: 'bg-emerald-200',
    heroBorder: 'border-emerald-300',
    heroBadgeBg: 'bg-emerald-500',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-emerald-700',
    primaryButtonHover: 'hover:bg-emerald-800',
    ctaSectionBg: 'bg-emerald-900',
    ctaButtonText: 'text-emerald-900',
  },
  '#7c3aed': {
    pageBg: 'bg-violet-50',
    navbarBorder: 'border-violet-300',
    heroBg: 'bg-violet-200',
    heroBorder: 'border-violet-300',
    heroBadgeBg: 'bg-violet-600',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-violet-700',
    primaryButtonHover: 'hover:bg-violet-800',
    ctaSectionBg: 'bg-violet-900',
    ctaButtonText: 'text-violet-900',
  },
  '#ea580c': {
    pageBg: 'bg-orange-50',
    navbarBorder: 'border-orange-300',
    heroBg: 'bg-orange-200',
    heroBorder: 'border-orange-300',
    heroBadgeBg: 'bg-orange-600',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-orange-700',
    primaryButtonHover: 'hover:bg-orange-800',
    ctaSectionBg: 'bg-orange-900',
    ctaButtonText: 'text-orange-900',
  },
  '#0f172a': {
    pageBg: 'bg-slate-100',
    navbarBorder: 'border-slate-300',
    heroBg: 'bg-slate-300',
    heroBorder: 'border-slate-400',
    heroBadgeBg: 'bg-slate-800',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-slate-800',
    primaryButtonHover: 'hover:bg-slate-900',
    ctaSectionBg: 'bg-slate-900',
    ctaButtonText: 'text-slate-900',
  },
  '#db2777': {
    pageBg: 'bg-pink-50',
    navbarBorder: 'border-pink-300',
    heroBg: 'bg-pink-200',
    heroBorder: 'border-pink-300',
    heroBadgeBg: 'bg-pink-600',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-pink-700',
    primaryButtonHover: 'hover:bg-pink-800',
    ctaSectionBg: 'bg-pink-900',
    ctaButtonText: 'text-pink-900',
  },
  '#16a34a': {
    pageBg: 'bg-green-50',
    navbarBorder: 'border-green-300',
    heroBg: 'bg-green-200',
    heroBorder: 'border-green-300',
    heroBadgeBg: 'bg-green-600',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-green-700',
    primaryButtonHover: 'hover:bg-green-800',
    ctaSectionBg: 'bg-green-900',
    ctaButtonText: 'text-green-900',
  },
  '#0891b2': {
    pageBg: 'bg-cyan-50',
    navbarBorder: 'border-cyan-300',
    heroBg: 'bg-cyan-200',
    heroBorder: 'border-cyan-300',
    heroBadgeBg: 'bg-cyan-600',
    heroBadgeText: 'text-white',
    primaryButtonBg: 'bg-cyan-700',
    primaryButtonHover: 'hover:bg-cyan-800',
    ctaSectionBg: 'bg-cyan-900',
    ctaButtonText: 'text-cyan-900',
  },
}

const buildCartTotals = (items: CartItem[]) => {
  const totalQty = items.reduce((sum, i) => sum + i.quantity, 0)
  const totalPrice = items.reduce((sum, i) => sum + getUnitPrice(i.product) * i.quantity, 0)
  return { totalQty, totalPrice }
}

const buildWhatsAppInvoice = (brandName: string, items: CartItem[]) => {
  if (items.length === 0) {
    return `Halo kak, saya ingin bertanya tentang katalog *${brandName}*`
  }
  const { totalQty, totalPrice } = buildCartTotals(items)
  const lines: string[] = [
    `Halo kak, saya ingin pesan dari *${brandName}*:`,
    '',
    '*RINGKASAN PESANAN*',
    '────────────────',
  ]
  items.forEach((item, index) => {
    const unit = getUnitPrice(item.product)
    const subtotal = unit * item.quantity
    const categoryLabel = item.product.brandName ? ` (${item.product.brandName})` : ''
    lines.push(`${index + 1}. *${item.product.name}*${categoryLabel}`)
    lines.push(`   ${item.quantity} x ${formatRupiah(unit)} = *${formatRupiah(subtotal)}*`)
  })
  lines.push('────────────────')
  lines.push(`*Total item:* ${totalQty} produk`)
  lines.push(`*Total harga:* *${formatRupiah(totalPrice)}*`)
  lines.push('', 'Mohon konfirmasi ketersediaan & cara pembayaran. Terima kasih!')
  return lines.join('\n')
}

const buildWaLink = (whatsapp: string, message: string) => {
  const phone = whatsapp.replace(/[^0-9]/g, '')
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
}

// Animation variants
const fadeInUp: any = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
}

const staggerContainer: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
}
/** URL marketplace / eksternal untuk href (tambah https jika user hanya isi domain) */
const normalizePublicUrl = (raw?: string | null) => {
  if (!raw || typeof raw !== 'string') return null
  const t = raw.trim()
  if (!t) return null
  if (t.startsWith('http://') || t.startsWith('https://')) return t
  return `https://${t}`
}

// Loading Skeleton
const SkeletonCard = () => (
  <motion.div 
    className="bg-white rounded-2xl overflow-hidden shadow-lg"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
  >
    <div className="h-56 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"></div>
    <div className="p-5 space-y-3">
      <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
      <div className="flex items-center justify-between pt-2">
        <div className="h-6 bg-gray-200 rounded w-24 animate-pulse"></div>
        <div className="h-8 bg-gray-200 rounded w-20 animate-pulse"></div>
      </div>
    </div>
  </motion.div>
)

export default function LandingPage({ forcedSlug }: { forcedSlug?: string } = {}) {
  const { user } = useAuth()
  const [scrollY, setScrollY] = useState(0)
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [modalQty, setModalQty] = useState(1)
  const [cart, setCart] = useState<Record<number, CartItem>>({})
  const [showInvoice, setShowInvoice] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [heroImageIndex, setHeroImageIndex] = useState(0)
  const [logoFailed, setLogoFailed] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const { subdomain: routeSlug } = useParams()
  const subdomain = forcedSlug || routeSlug
  const brochureRef = useRef<HTMLDivElement>(null)
  
  const catalogUrl = typeof window !== 'undefined' ? window.location.href : ''

  // Download brochure as image
  const downloadBrochure = async () => {
    if (!brochureRef.current) return
    
    setIsDownloading(true)
    try {
      const dataUrl = await toPng(brochureRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: false,
        preferredFontFormat: 'woff2'
      })
      
      const link = document.createElement('a')
      link.download = `katalog-${brand?.name || 'produk'}-${new Date().toISOString().split('T')[0]}.png`
      link.href = dataUrl
      link.click()
    } catch (error) {
      console.error('Failed to download brochure:', error)
      alert('Gagal mendownload brosur. Silakan coba lagi.')
    } finally {
      setIsDownloading(false)
    }
  }
  
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  const { data: catalogData, isLoading: catalogLoading } = useQuery<GroupedCatalog>({
    queryKey: ['catalog-grouped', subdomain],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/catalog/${subdomain}/grouped`)
      if (!response.ok) throw new Error('Brand not found')
      return response.json()
    },
    enabled: !!subdomain,
    staleTime: 0,
  })

  const brand = catalogData?.brand
  const brandLoading = catalogLoading
  const catalogCategories = catalogData?.categories ?? []
  const hasMultipleCategories = catalogCategories.length > 1
  const products = catalogData?.products
  const productsLoading = catalogLoading

  const handleWhatsAppClick = () => {
    if (brand?.id) trackWhatsAppClick(brand.id)
  }

  const openProductModal = (product: Product) => {
    setSelectedProduct(product)
    setModalQty(cart[product.id]?.quantity ?? 1)
    setCurrentImageIndex(0)
    trackProductView(product.id)
  }

  const planId = normalizePlanId(brand?.plan)
  const productSeoEnabled = canUseFeature(planId, 'product_seo')
  const themeColor =
    brand?.colorTheme && canUseFeature(planId, 'custom_theme')
      ? brand.colorTheme
      : DEFAULT_THEME
  const activeTheme = THEME_STYLES[themeColor] || THEME_STYLES[DEFAULT_THEME]
  const catalogTagline =
    planId === 'premium'
      ? 'Katalog Premium · Tampil Lebih Profesional'
      : planId === 'basic'
        ? 'UMKM Terverifikasi'
        : 'Toko Online Terpercaya'
  const trustBadges =
    planId === 'premium'
      ? ['Produk Berkualitas', 'Katalog Premium', 'Subdomain Usaha', 'Layanan Ramah', 'Aman & Terpercaya']
      : planId === 'basic'
        ? ['Produk Berkualitas', 'UMKM Terverifikasi', 'Harga Terjangkau', 'Layanan Ramah', 'Aman & Terpercaya']
        : ['Produk Berkualitas', 'Harga Terjangkau', 'Pengiriman Cepat', 'Layanan Ramah', 'Aman & Terpercaya']
  const activeProducts = Array.isArray(products) ? products.filter((p: Product) => p.isActive !== false) : []
  const hasProducts = activeProducts.length > 0
  const brochureProducts = activeProducts.slice(0, 6)
  const activeCategorySlug = catalogData?.activeCategorySlug
  const selectedCategory = selectedCategorySlug
    ? catalogCategories.find((c) => c.slug === selectedCategorySlug) ?? null
    : null
  const visibleCategories = hasMultipleCategories
    ? selectedCategorySlug
      ? catalogCategories.filter((c) => c.slug === selectedCategorySlug)
      : catalogCategories
    : catalogCategories

  const selectCategory = (slug: string | null) => {
    setSelectedCategorySlug(slug)
    const base = window.location.pathname + window.location.search
    if (slug) {
      window.history.replaceState(null, '', `${base}#kategori-${slug}`)
    } else {
      window.history.replaceState(null, '', base)
    }
  }

  useEffect(() => {
    if (brandLoading || !hasMultipleCategories) return
    const hash = window.location.hash.replace(/^#kategori-/, '')
    if (hash) {
      setSelectedCategorySlug(hash)
    } else if (activeCategorySlug) {
      setSelectedCategorySlug(activeCategorySlug)
    }
  }, [brandLoading, hasMultipleCategories, activeCategorySlug, catalogCategories.length])

  useEffect(() => {
    if (brand?.slug && brand?.name) {
      const canonical = brand.canonicalUrl || `${window.location.origin}/catalog/${brand.slug}`
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.rel = 'canonical'
        document.head.appendChild(link)
      }
      link.href = canonical

      applyCatalogSeo({
        name: brand.name,
        slug: brand.slug,
        description: brand.description,
        logoUrl: brand.logoUrl,
        address: brand.address,
        whatsapp: brand.whatsapp,
      })
    }
    return () => clearCatalogSeo()
  }, [brand?.id, brand?.name, brand?.slug, brand?.description, brand?.logoUrl, brand?.address, brand?.whatsapp])

  // Increment view count when page loads (only once per session)
  useEffect(() => {
    if (brand?.id && !brandLoading) {
      console.log('Incrementing view for brand:', brand.id)
      incrementBrandView(brand.id).then(() => {
        console.log('View count incremented successfully')
      }).catch(err => {
        console.error('Failed to increment view:', err)
      })
    }
  }, [brand?.id, brandLoading])

  const heroProductId = activeProducts[0]?.id

  useEffect(() => {
    setHeroImageIndex(0)
  }, [heroProductId])

  useEffect(() => {
    setLogoFailed(false)
  }, [brand?.logoUrl])

  // Loading state
  if (brandLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-gray-200 rounded-2xl"></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} />)}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (!brand) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Store size={64} className="mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-600">Brand tidak ditemukan</h1>
          <p className="text-gray-400 mt-2">Coba periksa kembali URL yang Anda masukkan</p>
        </div>
      </div>
    )
  }

  const heroProduct = activeProducts[0]
  const heroImages = heroProduct?.images?.filter(Boolean) ?? []

  const tokopediaHref = normalizePublicUrl(brand.tokopediaUrl)
  const shopeeHref = normalizePublicUrl(brand.shopeeUrl)
  const lazadaHref = normalizePublicUrl(brand.lazadaUrl)
  const foodChannels = getFoodChannels(brand)

  const cartItems = Object.values(cart)
  const { totalQty, totalPrice } = buildCartTotals(cartItems)
  const hasCart = cartItems.length > 0
  const waMessage = buildWhatsAppInvoice(brand.name, cartItems)
  const waLink = brand.whatsapp ? buildWaLink(brand.whatsapp, waMessage) : ''
  const canCheckout = Boolean(waLink || foodChannels.length > 0)

  const handleFoodOrder = async (channel: FoodChannel) => {
    const invoice = buildWhatsAppInvoice(brand.name, cartItems)
    const note = `\n\nPesanan dari katalog online. Silakan pilih menu yang sama di ${channel.label}.`
    try {
      await navigator.clipboard.writeText(invoice + note)
      toast.success('Ringkasan pesanan disalin. Buka aplikasi untuk melanjutkan.')
    } catch {
      toast.message('Membuka toko di ' + channel.label)
    }
    openFoodStore(channel.url)
  }

  const isInCart = (productId: number) => Boolean(cart[productId])

  const toggleCartItem = (product: Product) => {
    setCart((prev) => {
      const next = { ...prev }
      if (next[product.id]) delete next[product.id]
      else next[product.id] = { product, quantity: 1 }
      return next
    })
  }

  const setCartQuantity = (productId: number, quantity: number) => {
    setCart((prev) => {
      const item = prev[productId]
      if (!item) return prev
      if (quantity < 1) {
        const next = { ...prev }
        delete next[productId]
        return next
      }
      return { ...prev, [productId]: { ...item, quantity } }
    })
  }

  const addToCart = (product: Product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev[product.id]
      return {
        ...prev,
        [product.id]: {
          product,
          quantity: (existing?.quantity ?? 0) + quantity,
        },
      }
    })
  }

  const clearCart = () => setCart({})

  const renderProductGrid = (items: Product[]) => (
    <motion.div
      className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4"
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <AnimatePresence>
        {items.map((product) => {
          const productCatalogSlug = product.brandSlug || brand?.slug
          return (
            <motion.div
              key={product.id}
              variants={fadeInUp}
              className={`group relative rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl border-2 bg-white ${
                isInCart(product.id) ? 'border-[#25D366] ring-2 ring-[#25D366]/30' : 'border-gray-200'
              }`}
              whileHover={{ y: -6, scale: 1.01 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className="relative aspect-square overflow-hidden cursor-pointer bg-gradient-to-br from-gray-100 to-gray-200"
                onClick={() => openProductModal(product)}
                onKeyDown={(e) => e.key === 'Enter' && openProductModal(product)}
                role="button"
                tabIndex={0}
                aria-label={`Lihat detail ${product.name}`}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleCartItem(product)
                  }}
                  className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                    isInCart(product.id) ? 'bg-[#25D366] text-white' : 'bg-white/95 text-gray-600 hover:bg-white'
                  }`}
                  title={isInCart(product.id) ? 'Batalkan pilihan' : 'Pilih produk'}
                >
                  {isInCart(product.id) ? <Check size={18} /> : <Plus size={18} />}
                </button>
                {product.images && product.images.length > 0 ? (
                  <img
                    src={getImageUrl(product.images[0]) || ''}
                    alt={product.name}
                    className="w-full h-full object-cover pointer-events-none select-none"
                    draggable={false}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag size={64} style={{ color: themeColor }} className="opacity-40" />
                  </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent pointer-events-none" />

                <AnimatePresence>
                  {product.discountPrice && product.discountPrice < product.price && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0, x: -20 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      className="absolute top-4 left-4 bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg"
                    >
                      DISKON {Math.round((1 - product.discountPrice / product.price) * 100)}%
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="absolute bottom-3 left-3 right-3 z-10 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 pointer-events-none sm:pointer-events-auto">
                  {productSeoEnabled && product.slug && productCatalogSlug ? (
                    <Link
                      to={getProductCatalogUrl(productCatalogSlug, product.slug)}
                      onClick={(e) => e.stopPropagation()}
                      className="block w-full py-2.5 bg-white/95 backdrop-blur-sm rounded-xl text-sm font-semibold text-gray-800 shadow-lg text-center pointer-events-auto"
                    >
                      Halaman Produk
                    </Link>
                  ) : (
                    <span className="block w-full py-2.5 bg-white/95 backdrop-blur-sm rounded-xl text-sm font-semibold text-gray-800 shadow-lg text-center sm:hidden">
                      Ketuk untuk detail
                    </span>
                  )}
                </div>
              </div>

              <div className="p-3">
                {productSeoEnabled && product.slug && productCatalogSlug ? (
                  <Link to={getProductCatalogUrl(productCatalogSlug, product.slug)}>
                    <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-700 transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                ) : (
                  <h3
                    className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-700 transition-colors cursor-pointer"
                    onClick={() => openProductModal(product)}
                  >
                    {product.name}
                  </h3>
                )}

                {product.description && (
                  <p className="text-xs text-gray-500 mb-2 line-clamp-1">
                    {product.description}
                  </p>
                )}

                <div className="flex items-end justify-between pt-2 border-t border-gray-100 gap-2">
                  <div>
                    {product.discountPrice ? (
                      <>
                        <div className="text-lg font-black" style={{ color: themeColor }}>
                          {formatRupiah(product.discountPrice)}
                        </div>
                        <div className="text-[11px] text-gray-400 line-through">
                          {formatRupiah(product.price)}
                        </div>
                      </>
                    ) : (
                      <div className="text-lg font-black" style={{ color: themeColor }}>
                        {formatRupiah(product.price)}
                      </div>
                    )}
                  </div>
                  {isInCart(product.id) && (
                    <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                      <button
                        type="button"
                        onClick={() => setCartQuantity(product.id, cart[product.id].quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center text-sm font-bold">{cart[product.id].quantity}</span>
                      <button
                        type="button"
                        onClick={() => setCartQuantity(product.id, cart[product.id].quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </motion.div>
  )

  return (
    <div className={`min-h-screen ${activeTheme.pageBg} text-slate-800 ${hasCart ? 'pb-44 md:pb-36' : ''}`}>
      {/* Navbar */}
      <motion.nav
        className={`relative z-40 sticky top-0 bg-white border-b ${activeTheme.navbarBorder} ${scrollY > 6 ? 'shadow-md' : 'shadow-sm'}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="min-h-[4.5rem] py-2 sm:py-0 sm:h-20 flex items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div
                className="w-12 h-12 rounded-full border border-gray-200 overflow-hidden bg-white flex items-center justify-center shrink-0"
              >
                {brand.logoUrl && !logoFailed ? (
                  <img
                    src={getImageUrl(brand.logoUrl) || ''}
                    alt={brand.name}
                    className="w-full h-full object-contain p-0.5"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: themeColor }}
                  >
                    {brand.name?.charAt(0).toUpperCase() || <Store size={20} />}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                {brand.badge && (
                  <span
                    className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full mb-1 ${
                      brand.badge === 'Premium' ? 'bg-amber-500 text-white' : 'bg-blue-600 text-white'
                    }`}
                  >
                    {brand.badge}
                  </span>
                )}
                <h1 className="text-base md:text-xl font-extrabold text-slate-800 line-clamp-2 leading-snug break-words">
                  {brand.name}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">{catalogTagline}</p>
                {planId === 'premium' && brand.subdomainUrl && (
                  <a
                    href={brand.subdomainUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-amber-700 font-mono truncate mt-0.5 block hover:underline"
                  >
                    {brand.subdomainUrl.replace('https://', '')}
                  </a>
                )}
              </div>
            </div>

            <div className="hidden md:flex items-center gap-3 text-sm text-gray-500">
              {brand.address && (
                <div className="flex items-center gap-1 max-w-[280px] truncate">
                  <MapPin size={14} className="shrink-0" />
                  <span className="truncate">{brand.address}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 max-w-[min(100%,22rem)] sm:max-w-none">
              {brand.instagram && (
                <a
                  href={`https://instagram.com/${brand.instagram.startsWith('@') ? brand.instagram.slice(1) : brand.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 shrink-0 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center"
                >
                  <Instagram size={16} />
                </a>
              )}
              {brand.facebook && (
                <a
                  href={`https://facebook.com/${brand.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 shrink-0 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"
                >
                  <Facebook size={16} />
                </a>
              )}
              {brand.tiktok && (
                <a
                  href={`https://tiktok.com/@${brand.tiktok.startsWith('@') ? brand.tiktok.slice(1) : brand.tiktok}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 shrink-0 rounded-full bg-slate-900 text-white flex items-center justify-center"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              )}
              {tokopediaHref && (
                <a
                  href={tokopediaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Tokopedia"
                  aria-label="Buka toko di Tokopedia"
                  className="shrink-0 flex size-10 items-center justify-center rounded-xl border border-green-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-green-300/50 transition-shadow"
                >
                  <TokopediaIcon className="size-8" />
                </a>
              )}
              {shopeeHref && (
                <a
                  href={shopeeHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Shopee"
                  aria-label="Buka toko di Shopee"
                  className="shrink-0 flex size-10 items-center justify-center rounded-xl border border-orange-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-orange-300/50 transition-shadow"
                >
                  <ShopeeIcon className="size-8" />
                </a>
              )}
              {lazadaHref && (
                <a
                  href={lazadaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Lazada"
                  aria-label="Buka toko di Lazada"
                  className="shrink-0 flex size-10 items-center justify-center rounded-xl border border-blue-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-blue-300/50 transition-shadow"
                >
                  <LazadaIcon className="size-8" />
                </a>
              )}
              {foodChannels.map((ch) => (
                <a
                  key={ch.id}
                  href={ch.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={ch.label}
                  aria-label={`Buka toko di ${ch.label}`}
                  className="shrink-0 flex size-10 items-center justify-center rounded-xl border border-gray-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-gray-300/50 transition-shadow"
                >
                  <FoodDeliveryIcon channel={ch.id} className="size-8" />
                </a>
              ))}
              {user && (
                <>
                  <Link
                    to="/dashboard"
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span className="hidden sm:inline">Dashboard</span>
                  </Link>
                  <Link
                    to="/marketing"
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-100"
                  >
                    <Megaphone className="w-4 h-4" />
                    <span className="hidden sm:inline">Marketing</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Promo Banner & Video (Premium) */}
      {planId === 'premium' && (brand.promoBannerUrl || brand.promoVideoUrl) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 space-y-4">
          {brand.promoBannerUrl && (
            <img
              src={getImageUrl(brand.promoBannerUrl) || ''}
              alt={`Promo ${brand.name}`}
              className="w-full rounded-2xl object-cover max-h-48 md:max-h-64 shadow-md"
            />
          )}
          {brand.promoVideoUrl && (
            <div className="rounded-2xl overflow-hidden shadow-md bg-black/5">
              <video
                src={getImageUrl(brand.promoVideoUrl) || ''}
                className="w-full max-h-72 md:max-h-96 object-contain bg-black"
                controls
                playsInline
                preload="metadata"
              />
            </div>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10">
        <div className={`rounded-3xl overflow-hidden ${activeTheme.heroBg} border ${activeTheme.heroBorder}`}>
          <div className="grid lg:grid-cols-2 gap-4 items-center p-6 md:p-10">
            <div>
              <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${activeTheme.heroBadgeBg} ${activeTheme.heroBadgeText} mb-4`}>
                PRODUK UNGGULAN
              </span>
              <h2 className="text-3xl md:text-5xl font-extrabold leading-tight text-slate-900 mb-4">
                Kualitas Terbaik
                <br />
                Untuk Anda
              </h2>
              <p className="text-slate-600 max-w-xl mb-6">
                {brand.description || 'Produk pilihan dengan kualitas terbaik untuk memenuhi kebutuhan Anda sehari-hari.'}
              </p>
              <div className="grid grid-cols-3 gap-3 text-xs md:text-sm text-slate-700 mb-7">
                <div className="flex flex-col gap-1">
                  <Star size={16} className="text-amber-500" />
                  <span>100% Original</span>
                </div>
                <div className="flex flex-col gap-1">
                  <Sparkles size={16} className="text-blue-500" />
                  <span>Kualitas Terjamin</span>
                </div>
                <div className="flex flex-col gap-1">
                  <Zap size={16} className="text-green-500" />
                  <span>Pengiriman Cepat</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {brand.whatsapp && (
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleWhatsAppClick}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366] text-white font-semibold shadow-sm"
                  >
                    <Phone size={18} />
                    {hasCart ? `Chat WA (${totalQty} item)` : 'Chat WhatsApp'}
                  </a>
                )}
                <a
                  href="#katalog-produk"
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl ${activeTheme.primaryButtonBg} text-white font-semibold shadow-sm ${activeTheme.primaryButtonHover}`}
                >
                  Lihat Produk
                  <ChevronRight size={18} />
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden bg-white shadow-lg border border-white/70">
                {heroImages.length > 0 ? (
                  <>
                    <img
                      src={getImageUrl(heroImages[heroImageIndex]) || ''}
                      alt={heroProduct?.name}
                      className="w-full h-[240px] md:h-[340px] object-cover"
                    />
                    {planId === 'premium' && heroImages.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setHeroImageIndex((i) => (i === 0 ? heroImages.length - 1 : i - 1))
                          }
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-white"
                          aria-label="Gambar sebelumnya"
                        >
                          <ChevronRight size={20} className="rotate-180" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setHeroImageIndex((i) => (i === heroImages.length - 1 ? 0 : i + 1))
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-white"
                          aria-label="Gambar berikutnya"
                        >
                          <ChevronRight size={20} />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {heroImages.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setHeroImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === heroImageIndex ? 'bg-indigo-600' : 'bg-gray-300'
                              }`}
                              aria-label={`Gambar ${idx + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[240px] md:h-[340px] bg-gradient-to-br from-yellow-200 to-amber-100 flex items-center justify-center">
                    <Store size={72} style={{ color: themeColor }} />
                  </div>
                )}
              </div>
              <span className="absolute top-4 right-4 px-4 py-2 rounded-full bg-amber-400 text-slate-900 text-xs font-bold">
                BEST SELLER
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          {trustBadges.map((item) => (
            <div key={item} className="text-center text-sm font-medium text-slate-700">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
                <ShoppingBag size={16} className="text-amber-500" />
              </div>
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Products Section */}
      <main id="katalog-produk" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium mb-4"
            style={{ 
              background: `linear-gradient(135deg, ${themeColor}20 0%, ${themeColor}10 100%)`,
              color: themeColor,
              border: `1px solid ${themeColor}30`
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <ShoppingBag size={18} />
            <span>Katalog Produk</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            {selectedCategory
              ? selectedCategory.name
              : hasMultipleCategories
                ? 'Kategori & Produk Kami'
                : 'Produk Pilihan Kami'}
          </h2>
          <p className="text-gray-500 max-w-lg mx-auto">
            {hasProducts
              ? hasMultipleCategories
                ? selectedCategory
                  ? `Produk dari kategori ${selectedCategory.name}.`
                  : 'Pilih kategori brand untuk melihat produknya.'
                : 'Pilih satu atau lebih produk (tombol +), lalu kirim ringkasan pesanan lewat WhatsApp.'
              : 'Seller sedang menyiapkan katalog produk terbaik'}
          </p>
        </motion.div>

        {hasMultipleCategories && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            <button
              type="button"
              onClick={() => selectCategory(null)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                !selectedCategorySlug
                  ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                  : 'bg-white border-gray-200 text-slate-700 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <ShoppingBag size={16} />
              Semua
            </button>
            {catalogCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => selectCategory(category.slug)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                  selectedCategorySlug === category.slug
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-white border-gray-200 text-slate-700 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                {category.logoUrl || brand?.logoUrl ? (
                  <img
                    src={getImageUrl(category.logoUrl || brand?.logoUrl) || ''}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <Store size={16} className={selectedCategorySlug === category.slug ? 'text-white' : 'text-gray-400'} />
                )}
                {category.name}
              </button>
            ))}
          </div>
        )}
        
        {/* Products Grid */}
        {productsLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : hasProducts ? (
          hasMultipleCategories ? (
            <div className="space-y-14">
              {visibleCategories.map((category) => {
                const categoryProducts = category.products.filter((p) => p.isActive !== false)
                if (categoryProducts.length === 0) return null
                return (
                  <section key={category.id} id={`kategori-${category.slug}`} className="scroll-mt-28">
                    {!selectedCategorySlug && (
                    <div className="flex items-start gap-4 mb-6 pb-4 border-b border-gray-200">
                      <div className="w-14 h-14 rounded-xl border border-gray-200 bg-white overflow-hidden flex items-center justify-center shrink-0">
                        {category.logoUrl || brand?.logoUrl ? (
                          <img
                            src={getImageUrl(category.logoUrl || brand?.logoUrl) || ''}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Store size={24} className="text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900">{category.name}</h3>
                        {category.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{category.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{categoryProducts.length} produk</p>
                      </div>
                    </div>
                    )}
                    {renderProductGrid(categoryProducts)}
                  </section>
                )
              })}
            </div>
          ) : (
            renderProductGrid(activeProducts)
          )
        ) : (
          <motion.div 
            className="text-center py-24"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <motion.div
              className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
              style={{ background: `linear-gradient(135deg, ${themeColor}20 0%, ${themeColor}10 100%)` }}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <ShoppingBag size={48} style={{ color: themeColor }} className="opacity-50" />
            </motion.div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">Belum ada produk</h3>
            <p className="text-gray-400 max-w-md mx-auto">Seller sedang menyiapkan katalog produk terbaik untuk Anda</p>
          </motion.div>
        )}
        {hasProducts && (
          <p className="text-center text-sm text-gray-500 mt-6">
            Ketuk <strong>+</strong> pada produk untuk memilih item, lalu pesan lewat WhatsApp atau GrabFood / GoFood / ShopeeFood.
          </p>
        )}
      </main>

      {/* Keranjang & invoice — sticky */}
      <AnimatePresence>
        {hasCart && canCheckout && (
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 md:px-6"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
          >
            <div className="max-w-3xl mx-auto rounded-2xl bg-white border border-gray-200 shadow-2xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowInvoice((v) => !v)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 ${activeTheme.ctaSectionBg} text-white text-left`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <ClipboardList size={20} className="shrink-0" />
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">
                      {cartItems.length} produk · {totalQty} item
                    </p>
                    <p className="text-xs text-blue-100">Total {formatRupiah(totalPrice)}</p>
                  </div>
                </div>
                <ChevronRight
                  size={20}
                  className={`shrink-0 transition-transform ${showInvoice ? 'rotate-90' : ''}`}
                />
              </button>

              <AnimatePresence>
                {showInvoice && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden border-t border-gray-100"
                  >
                    <div className="max-h-48 overflow-y-auto px-4 py-3 text-sm space-y-2">
                      {cartItems.map((item, idx) => {
                        const unit = getUnitPrice(item.product)
                        const sub = unit * item.quantity
                        return (
                          <div key={item.product.id} className="flex justify-between gap-2 border-b border-gray-50 pb-2 last:border-0">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-800 truncate">
                                {idx + 1}. {item.product.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.quantity} × {formatRupiah(unit)}
                              </p>
                            </div>
                            <p className="font-semibold text-gray-900 shrink-0">{formatRupiah(sub)}</p>
                          </div>
                        )
                      })}
                      <div className="flex justify-between font-bold pt-1" style={{ color: themeColor }}>
                        <span>Total</span>
                        <span>{formatRupiah(totalPrice)}</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="p-3 bg-gray-50 space-y-2">
                <p className="text-xs font-medium text-gray-600 text-center">Pilih cara pesan</p>
                <div className="flex flex-col gap-2">
                  {waLink && (
                    <a
                      href={waLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#25D366] text-white font-bold text-sm shadow-md hover:bg-[#20bd5a]"
                    >
                      <MessageCircle size={18} />
                      WhatsApp + Invoice
                    </a>
                  )}
                  {foodChannels.map((ch) => (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => handleFoodOrder(ch)}
                      className="w-full flex items-center justify-center gap-3 py-2.5 px-3 rounded-xl bg-white border-2 border-gray-200 text-sm text-gray-800 hover:bg-gray-50"
                    >
                      <FoodDeliveryIcon channel={ch.id} showLabel />
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={clearCart}
                  className="w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-xl border border-gray-200 hover:bg-white"
                >
                  Kosongkan keranjang
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <motion.section
        className="relative py-12 overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className={`absolute inset-0 ${activeTheme.ctaSectionBg}`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white">
            <div>
              <h2 className="text-3xl md:text-4xl font-black">Hubungi kami sekarang!</h2>
              <p className="text-blue-100 mt-2">Kami siap membantu Anda untuk mendapatkan produk terbaik dengan pelayanan ramah.</p>
            </div>
          {brand.whatsapp && (
            <motion.a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-3 px-6 py-3 bg-white ${activeTheme.ctaButtonText} text-base font-bold rounded-xl shadow-xl`}
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <Phone size={24} />
              {hasCart ? `Pesan ${totalQty} item via WA` : 'Chat WhatsApp Sekarang'}
            </motion.a>
          )}
          </div>
        </div>
      </motion.section>

      {/* Product Detail Modal */}
      <AnimatePresence mode="wait">
        {selectedProduct && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setSelectedProduct(null)}
              aria-hidden
            />

            <motion.div
              className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-2xl leading-none">&times;</span>
              </button>

              {/* Image Gallery */}
              <div className="relative h-80 bg-gray-900 overflow-hidden">
                {selectedProduct.images && selectedProduct.images.length > 0 ? (
                  <>
                    <img
                      src={getImageUrl(selectedProduct.images[currentImageIndex]) || ''}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Image Navigation */}
                    {selectedProduct.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(prev => prev === 0 ? selectedProduct.images!.length - 1 : prev - 1)}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronRight size={20} className="rotate-180" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(prev => prev === selectedProduct.images!.length - 1 ? 0 : prev + 1)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                        >
                          <ChevronRight size={20} />
                        </button>
                        
                        {/* Image Indicators */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {selectedProduct.images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                        
                        {/* Image Counter */}
                        <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {selectedProduct.images.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ShoppingBag size={80} className="text-gray-300" />
                  </div>
                )}
              </div>
              
              {/* Product Info */}
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {selectedProduct.name}
                </h2>
                
                {selectedProduct.description && (
                  <p className="text-gray-600 mb-4">
                    {selectedProduct.description}
                  </p>
                )}
                
                {/* Price */}
                <div className="flex items-center gap-3 mb-6">
                  {selectedProduct.discountPrice && selectedProduct.discountPrice < selectedProduct.price ? (
                    <>
                      <span className="text-3xl font-black" style={{ color: themeColor }}>
                        {formatRupiah(selectedProduct.discountPrice)}
                      </span>
                      <span className="text-lg text-gray-400 line-through">
                        {formatRupiah(selectedProduct.price)}
                      </span>
                      <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        -{Math.round((1 - selectedProduct.discountPrice / selectedProduct.price) * 100)}%
                      </span>
                    </>
                  ) : (
                    <span className="text-3xl font-black" style={{ color: themeColor }}>
                      {formatRupiah(selectedProduct.price)}
                    </span>
                  )}
                </div>
                
                {/* Jumlah */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm font-medium text-gray-600">Jumlah:</span>
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 p-1">
                    <button
                      type="button"
                      onClick={() => setModalQty((q) => Math.max(1, q - 1))}
                      className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="w-10 text-center font-bold">{modalQty}</span>
                    <button
                      type="button"
                      onClick={() => setModalQty((q) => q + 1)}
                      className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      addToCart(selectedProduct, modalQty)
                      setSelectedProduct(null)
                    }}
                    className={`flex-1 py-3 ${activeTheme.primaryButtonBg} text-white rounded-xl font-semibold ${activeTheme.primaryButtonHover} transition-colors`}
                  >
                    Tambah ke Pesanan ({modalQty})
                  </button>
                  {brand.whatsapp && (
                    <motion.a
                      href={buildWaLink(
                        brand.whatsapp,
                        buildWhatsAppInvoice(brand.name, [
                          ...cartItems.filter((i) => i.product.id !== selectedProduct.id),
                          { product: selectedProduct, quantity: modalQty },
                        ])
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 text-white rounded-xl font-semibold shadow-lg hover:bg-green-600 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Phone size={20} />
                      Langsung Chat WA
                    </motion.a>
                  )}
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowShareModal(false)}
            />
            
            <motion.div 
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
            >
              <button
                onClick={() => setShowShareModal(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <span className="text-xl leading-none">&times;</span>
              </button>
              
              <h3 className="text-xl font-bold text-gray-900 mb-4">Bagikan Katalog</h3>
              
              {/* Copy Link Section */}
              <div className="mb-6">
                <label className="text-sm text-gray-500 mb-2 block">Link Katalog</label>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={catalogUrl}
                    readOnly
                    className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600 truncate"
                  />
                  <motion.button
                    onClick={() => {
                      navigator.clipboard.writeText(catalogUrl)
                      setCopied(true)
                      setTimeout(() => setCopied(false), 2000)
                    }}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      copied ? 'bg-green-500 text-white' : 'bg-indigo-500 text-white'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </motion.button>
                </div>
              </div>
              
              {/* Share Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <motion.a
                  href={`https://wa.me/?text=${encodeURIComponent(`Lihat katalog produk ${brand.name}: ${catalogUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Phone size={20} />
                  WhatsApp
                </motion.a>
                
                <motion.a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(catalogUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Facebook size={20} />
                  Facebook
                </motion.a>
                
                <motion.a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Lihat katalog produk ${brand.name}`)}&url=${encodeURIComponent(catalogUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-xl font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <X size={20} />
                  X / Twitter
                </motion.a>
                
                <motion.button
                  onClick={() => {
                    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
                      navigator.share({
                        title: `Katalog ${brand.name}`,
                        text: `Lihat katalog produk ${brand.name}`,
                        url: catalogUrl
                      })
                    } else {
                      navigator.clipboard.writeText(catalogUrl)
                      setCopied(true)
                      setTimeout(() => setCopied(false), 2000)
                    }
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-indigo-500 text-white rounded-xl font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Share2 size={20} />
                  {typeof navigator !== 'undefined' && typeof navigator.share === 'function' ? 'Native Share' : 'Copy Link'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden Brochure Template (portrait) */}
      <div className="fixed -left-[99999px] top-0 pointer-events-none">
        <div
          ref={brochureRef}
          className="w-[1080px] min-h-[1620px] p-10"
          style={{
            background: 'linear-gradient(160deg, #0f2b60 0%, #244b96 42%, #f6c957 100%)'
          }}
        >
          <div className="rounded-[30px] bg-white/95 border border-white/80 shadow-2xl p-8">
            <div className="flex items-start justify-between gap-6 mb-8">
              <div className="flex items-center gap-4">
                {brand.logoUrl ? (
                  <img
                    src={getImageUrl(brand.logoUrl) || ''}
                    alt={brand.name}
                    className="w-20 h-20 rounded-2xl object-cover border border-slate-200"
                  />
                ) : (
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center text-white text-3xl font-bold"
                    style={{ backgroundColor: themeColor }}
                  >
                    {brand.name?.charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold tracking-[0.18em] text-[#0f2b60]">KATALOG PRODUK</p>
                  <h2 className="text-4xl font-black text-slate-900 mt-1">{brand.name}</h2>
                  <p className="text-sm text-slate-600 mt-2 max-w-[520px]">
                    {brand.description || 'Produk pilihan dengan kualitas terbaik untuk kebutuhan Anda.'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-slate-500">Alamat</p>
                <p className="text-sm font-semibold text-slate-800 max-w-[260px]">{brand.address || '-'}</p>
                {(tokopediaHref || shopeeHref || lazadaHref) && (
                  <div className="mt-3 flex flex-wrap justify-end gap-2" aria-label="Toko online marketplace">
                    {tokopediaHref && (
                      <span className="inline-flex rounded-xl border border-green-100/90 bg-white p-1 shadow-sm" title="Tokopedia">
                        <TokopediaIcon className="size-9" />
                      </span>
                    )}
                    {shopeeHref && (
                      <span className="inline-flex rounded-xl border border-orange-100/90 bg-white p-1 shadow-sm" title="Shopee">
                        <ShopeeIcon className="size-9" />
                      </span>
                    )}
                    {lazadaHref && (
                      <span className="inline-flex rounded-xl border border-blue-100/90 bg-white p-1 shadow-sm" title="Lazada">
                        <LazadaIcon className="size-9" />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-5">
              {brochureProducts.map((product) => (
                <div key={`brochure-${product.id}`} className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm">
                  <div className="h-[330px] bg-slate-100">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={getImageUrl(product.images[0]) || ''}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag size={54} className="text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-base font-bold text-slate-900 line-clamp-2 min-h-[3rem]">{product.name}</p>
                    <p className="text-xl font-black mt-2" style={{ color: themeColor }}>
                      {formatRupiah(product.discountPrice && product.discountPrice < product.price ? product.discountPrice : product.price)}
                    </p>
                    {product.discountPrice && product.discountPrice < product.price && (
                      <p className="text-xs text-slate-400 line-through mt-1">{formatRupiah(product.price)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl bg-[#0f2b60] text-white px-6 py-4 flex items-center justify-between">
              <p className="font-semibold">Hubungi kami untuk pemesanan produk</p>
              <p className="font-bold">{brand.whatsapp || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Download Button */}
      <motion.button
        onClick={downloadBrochure}
        disabled={isDownloading}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full shadow-xl font-medium disabled:opacity-50 backdrop-blur-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        {isDownloading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span className="hidden sm:inline">Mengunduh...</span>
          </>
        ) : (
          <>
            <Download size={20} />
            <span className="hidden sm:inline">Simpan Brosur</span>
          </>
        )}
      </motion.button>

      {/* Print/Brochure Styles */}
      <style>{`
        @media print {
          .fixed, .z-40, button {
            display: none !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>

      {/* Footer */}
      <footer className="relative bg-transparent text-slate-900 border-t border-slate-300/70">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="flex items-center gap-3 md:col-span-1">
              {brand.logoUrl ? (
                <img
                  src={getImageUrl(brand.logoUrl) || ''}
                  alt={brand.name}
                  className="h-12 w-12 rounded-full object-cover border border-gray-200"
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: themeColor }}
                >
                  <Store size={20} className="text-white" />
                </div>
              )}
              <div>
                <h4 className="font-bold text-lg text-slate-900">{brand.name}</h4>
                <p className="text-sm text-slate-700">Toko Online Terpercaya</p>
              </div>
            </div>

            <div>
              <h5 className="font-bold mb-3 text-slate-900">Tautan Cepat</h5>
              <ul className="space-y-2 text-sm text-slate-700">
                <li><a href="#katalog-produk" className="hover:text-slate-900">Katalog Produk</a></li>
                <li><a href="#" className="hover:text-slate-900">Tentang Kami</a></li>
                <li><a href="#" className="hover:text-slate-900">Cara Pemesanan</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold mb-3 text-slate-900">Informasi</h5>
              <ul className="space-y-2 text-sm text-slate-700">
                <li><a href="#" className="hover:text-slate-900">Kebijakan Privasi</a></li>
                <li><a href="#" className="hover:text-slate-900">Syarat & Ketentuan</a></li>
                <li><a href="#" className="hover:text-slate-900">Pengiriman</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold mb-3 text-slate-900">Ikuti Kami</h5>
              <div className="flex flex-wrap gap-2">
                {brand.instagram && (
                  <a
                    href={`https://instagram.com/${brand.instagram.startsWith('@') ? brand.instagram.slice(1) : brand.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center"
                  >
                    <Instagram size={16} />
                  </a>
                )}
                {brand.facebook && (
                  <a
                    href={`https://facebook.com/${brand.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center"
                  >
                    <Facebook size={16} />
                  </a>
                )}
                {brand.tiktok && (
                  <a
                    href={`https://tiktok.com/@${brand.tiktok.startsWith('@') ? brand.tiktok.slice(1) : brand.tiktok}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
                {tokopediaHref && (
                  <a
                    href={tokopediaHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Tokopedia"
                    aria-label="Buka toko di Tokopedia"
                    className="inline-flex size-11 items-center justify-center rounded-xl border border-green-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-green-300/50 transition-shadow"
                  >
                    <TokopediaIcon className="size-9" />
                  </a>
                )}
                {shopeeHref && (
                  <a
                    href={shopeeHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Shopee"
                    aria-label="Buka toko di Shopee"
                    className="inline-flex size-11 items-center justify-center rounded-xl border border-orange-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-orange-300/50 transition-shadow"
                  >
                    <ShopeeIcon className="size-9" />
                  </a>
                )}
                {lazadaHref && (
                  <a
                    href={lazadaHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Lazada"
                    aria-label="Buka toko di Lazada"
                    className="inline-flex size-11 items-center justify-center rounded-xl border border-blue-200/80 bg-white p-1 shadow-sm hover:ring-2 hover:ring-blue-300/50 transition-shadow"
                  >
                    <LazadaIcon className="size-9" />
                  </a>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-4">© {new Date().getFullYear()} {brand.name}</p>
              <p className="text-xs text-slate-600 mt-1">Powered by UMKM Indonesia</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
